import React, { useEffect, useMemo, useReducer } from 'react';
import { isNum } from '../../../utils/primitives';
import { ACTION_NEXT } from '../../../utils/elementActions';
import { loadCompletedSteps } from '../../../utils/init';
import SmoothBar from './components/SmoothBar';
import SegmentBar from './components/SegmentBar';
import StepperBar from './components/StepperBar';

function applyProgressBarStyles(responsiveStyles: any) {
  responsiveStyles.addTargets('barContainer', 'bar', 'barWrapper');

  responsiveStyles.applyFontStyles('barContainer');
  responsiveStyles.apply('barContainer', 'vertical_align', (a: any) => ({
    justifyContent: a
  }));
  responsiveStyles.apply('barContainer', 'horizontal_align', (a: any) => ({
    alignItems: a
  }));
  responsiveStyles.applyCorners('barWrapper');

  responsiveStyles.apply('bar', 'bar_color', (a: any) => ({
    backgroundColor: `#${a}`
  }));

  return responsiveStyles;
}

function ProgressBarElement({
  element,
  responsiveStyles,
  progress,
  curDepth = 1,
  maxDepth = 1,
  stepKey,
  runElementActions,
  client,
  elementProps = {},
  children
}: any) {
  const styles = useMemo(
    () => applyProgressBarStyles(responsiveStyles),
    [responsiveStyles]
  );

  const [, rerender] = useReducer((x) => x + 1, 0);
  const isStepper = !!element.properties?.stepper;
  // Fetch prior step completion the first time a stepper renders (deduped per
  // form inside loadCompletedSteps), then re-render this stepper to show it
  useEffect(() => {
    if (isStepper) loadCompletedSteps(client).then(() => rerender());
  }, [isStepper]);

  const vertical = element.styles.bar_direction === 'vertical';

  const containerProps = {
    css: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      width: '100%',
      ...(vertical && { height: '100%' }),
      ...styles.getTarget('barContainer')
    },
    // Fixed name: there is no content to derive one from
    name: 'progress_bar',
    ...elementProps
  };

  if (isStepper) {
    // Clicking a step navigates to it via a NEXT action without submitting.
    // Navigation to all steps makes any step clickable, otherwise only
    // already-visited steps are
    const allowAllNavigation = !!element.properties?.navigate_to_all_steps;
    // Display only; doesn't change the stored completion set.
    const resetCompletionOnBack =
      !!element.properties?.reset_completion_on_back;
    const stepConfigs = element.properties?.entries ?? [];
    const onStepClick = (config: any) =>
      runElementActions({
        element,
        elementType: 'progress_bar',
        actions: [{ type: ACTION_NEXT, next_step_key: config.step_key }],
        triggerPayload: {
          entryIndex: stepConfigs.indexOf(config),
          text: config.label
        }
      });
    return (
      <div {...containerProps}>
        {children}
        <StepperBar
          styles={styles}
          stepConfigs={stepConfigs}
          stepKey={stepKey}
          textPlacement={element.styles.percent_text_layout}
          onStepClick={onStepClick}
          allowAllNavigation={allowAllNavigation}
          resetCompletionOnBack={resetCompletionOnBack}
          vertical={vertical}
          style={vertical ? { flex: 1 } : undefined}
        />
      </div>
    );
  }

  let userProgress, userSegments;
  if (![null, undefined].includes(progress)) {
    if (typeof progress === 'number') {
      userProgress = progress;
    } else {
      userProgress = progress.progress;
      userSegments = progress.segments ?? element.properties.num_segments;
    }
  }
  userProgress = userProgress ?? element.properties?.progress;
  const percent = isNum(userProgress)
    ? userProgress
    : Math.round((100 * (curDepth + 1)) / ((maxDepth || 1) + 1));

  const textPlacement = element.styles.percent_text_layout;
  const showText = textPlacement && textPlacement !== 'none';

  const BarComponent = userSegments ? SegmentBar : SmoothBar;
  const progressBarElements: React.ReactNode[] = [
    <BarComponent
      key='progress'
      styles={styles}
      percent={percent}
      numSegments={userSegments}
      vertical={vertical}
    />
  ];
  const completionPercentage = vertical ? (
    <div
      key='completionPercentage'
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0 8px',
        whiteSpace: 'nowrap'
      }}
    >
      {`${percent}% completed`}
    </div>
  ) : (
    <div
      key='completionPercentage'
      style={{ width: '100%', textAlign: 'center' }}
    >
      {`${percent}% completed`}
    </div>
  );
  if (textPlacement === 'top') {
    progressBarElements.unshift(completionPercentage);
  } else if (textPlacement === 'bottom') {
    progressBarElements.push(completionPercentage);
  }

  // When vertical with text beside the bar, switch to row layout
  const flexDirection =
    vertical && showText ? ('row' as const) : ('column' as const);

  return (
    <div
      {...containerProps}
      css={{
        ...containerProps.css,
        flexDirection
      }}
    >
      {children}
      {progressBarElements}
    </div>
  );
}

export default ProgressBarElement;
